/**
 * The error contract the API actually returns (SPEC §11.2), as an RFC 9457
 * problem document.
 */
export interface ProblemDetails {
  type: string
  title: string
  status: number
  detail?: string
  /** Ties the response to a distributed trace. */
  traceId?: string
  /** The value a person can read off an error and quote to support. */
  correlationId?: string
  /** Field-level messages, present on validation failures. */
  errors?: Record<string, string[]>
}

/**
 * An API call that failed with a response the server described.
 *
 * Carries the problem document so callers can branch on `problemType` rather
 * than matching on message text, which breaks the moment copy is reworded.
 */
export class ApiError extends Error {
  // Explicit fields rather than constructor parameter properties: the Vite
  // tsconfig sets `erasableSyntaxOnly`, which forbids syntax that emits runtime
  // code so types can be stripped rather than compiled.
  readonly status: number
  readonly problem: ProblemDetails | null

  constructor(status: number, problem: ProblemDetails | null, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }

  /** The trailing segment of the problem type, e.g. `contribution-filled`. */
  get problemType(): string | null {
    if (!this.problem?.type) return null
    const parts = this.problem.type.split('/')
    return parts[parts.length - 1] || null
  }

  /** Another household claimed the remaining quantity (SPEC §6.20). */
  get isConflict(): boolean {
    return this.status === 409
  }

  /** An invite, recovery token, or household link that is no longer valid. */
  get isGone(): boolean {
    return this.status === 410
  }

  /** The household session or organizer sign-in is no longer valid. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  get isRateLimited(): boolean {
    return this.status === 429
  }

  /**
   * Wording safe to put in front of a family member.
   *
   * The server's `title` is written for people (SPEC §9.1 forbids words like
   * "token" in the UI), so it is preferred. The fallbacks exist for the cases
   * where there is no response to read at all.
   */
  get userMessage(): string {
    if (this.problem?.title) return this.problem.title
    if (this.status === 0) return "We couldn't reach Mabel's Table. Check your connection."
    if (this.status >= 500) return 'Something went wrong on our end. Please try again.'
    return 'Something went wrong. Please try again.'
  }
}
